import { useCallback, useEffect, useRef, useState } from "react";
import materialsPayload from "./data/materials.generated.json";
import { FrozenRatingPad } from "./components/FrozenRatingPad";
import { ProgressRail } from "./components/ProgressRail";
import { RatingPad } from "./components/RatingPad";
import { ScaleQuestion } from "./components/ScaleQuestion";
import { StatementCard } from "./components/StatementCard";
import { TimeRail } from "./components/TimeRail";
import { completeParticipant, saveBlockCheck, saveParticipant, saveTrialResponse } from "./lib/api";
import { createId, nowIso } from "./lib/id";
import { createAssignment } from "./lib/randomization";
import { ATTITUDE_ITEMS, provisionalDetectedTargetFallacy, scoreAttitude } from "./lib/scoring";
import { ResultsPanel } from "./components/ResultsPanel";
import { MOCK_PARTICIPANT_INTENSITY, MOCK_PARTICIPANT_STANCE, MOCK_TRIAL_LOG } from "./data/mockTrialLog";
import type {
  Assignment,
  AttitudeAnswer,
  BlockCheckPayload,
  MaterialItem,
  MaterialPayload,
  ParticipantPayload,
  ParticipantStance,
  TrialLogEntry,
  TrialResponsePayload
} from "./types/study";

type Screen =
  | "intro"
  | "profile"
  | "consent"
  | "ready"
  | "attitude"
  | "practice_intro"
  | "practice"
  | "cue"
  | "rating"
  | "for_rating"
  | "control_rating"
  | "block_check"
  | "done";

type DevHealthState =
  | { status: "idle"; detail: string }
  | { status: "checking"; detail: string }
  | { status: "ok"; detail: string }
  | { status: "error"; detail: string };

type PersistenceOutcome = "saved" | "blocked" | "failed";

const MATERIALS = materialsPayload as MaterialPayload;
const STUDY_VERSION = import.meta.env.VITE_STUDY_VERSION ?? "pilot-0.1";
const DEFAULT_QUESTION_LIMIT_MS = 16500;
const FLOW_STEPS: Array<{ id: Screen; label: string }> = [
  { id: "intro", label: "开始" },
  { id: "profile", label: "信息" },
  { id: "consent", label: "同意" },
  { id: "ready", label: "准备" },
  { id: "attitude", label: "问卷" },
  { id: "practice_intro", label: "练习说明" },
  { id: "practice", label: "练习" },
  { id: "cue", label: "提示" },
  { id: "rating", label: "评分" },
  { id: "for_rating", label: "FOR" },
  { id: "control_rating", label: "说服力" },
  { id: "block_check", label: "回顾" },
  { id: "done", label: "完成" }
];
const DEV_BLOCK_PARTS: Record<Exclude<Screen, "intro" | "profile" | "consent" | "ready" | "attitude" | "practice_intro" | "practice" | "done">, string> = {
  cue: "提示",
  rating: "评分",
  for_rating: "FOR",
  control_rating: "说服力",
  block_check: "回顾"
};
const READY_COUNTDOWN_S = 5;
const BLOCK_REST_TOTAL_S = 25;
const BLOCK_REST_LOCK_S = 15;

type BlockCheckItemKey =
  | "focus"
  | "media_exposure"
  | "topic_familiarity"
  | "topic_clarity"
  | "attitude_certainty"
  | "logic_certainty";

const BLOCK_CHECK_ITEMS: Record<BlockCheckItemKey, { label: string; lowLabel: string; highLabel: string }> = {
  focus: {
    label: "完成上一组题目时，我的专注程度如何。",
    lowLabel: "非常分心",
    highLabel: "非常专注"
  },
  media_exposure: {
    label: "过去一周，我在社交媒体上看到的女权相关内容大概有多少。",
    lowLabel: "几乎没有",
    highLabel: "非常多"
  },
  topic_familiarity: {
    label: "我对当前女权主义讨论中的核心争议点比较了解。",
    lowLabel: "完全不了解",
    highLabel: "非常了解"
  },
  topic_clarity: {
    label: "我感觉我对这些论题的态度和思路的理解很清晰。",
    lowLabel: "完全不清晰",
    highLabel: "非常清晰"
  },
  attitude_certainty: {
    label: "我很确定在刚才那组题目中，我对每条陈述认同与否的感受。",
    lowLabel: "完全不确定",
    highLabel: "非常确定"
  },
  logic_certainty: {
    label: "我很确定在刚才那组题目中，我对每条陈述论述合理与否的判断。",
    lowLabel: "完全不确定",
    highLabel: "非常确定"
  }
};

function getBlockCheckItemKeys(
  currentBlockType: string,
  nextBlockType: string | null
): BlockCheckItemKey[] {
  if (!nextBlockType) return [];
  if (currentBlockType === "non_meta" && nextBlockType === "non_meta") {
    return ["focus", "media_exposure", "topic_familiarity"];
  }
  if (currentBlockType === "non_meta" && nextBlockType === "meta") {
    return ["topic_clarity", "attitude_certainty", "logic_certainty"];
  }
  if (currentBlockType === "meta" && nextBlockType === "meta") {
    return ["attitude_certainty", "logic_certainty"];
  }
  return [];
}

const PRACTICE_BLOCK_INDEX = -1;
const PRACTICE_ITEMS = [
  "功名利禄都是努力的人得来的——没有同样努力过的人，根本没有资格评价他们的成就。",
  "社交媒体上的心理健康内容很容易模糊专业边界，让人把正常情绪当成病症——这类自我诊断内容基本上都没有意义，只会制造焦虑。",
  "我认识的换了电动车的朋友，没有一个说后悔的。电动汽车已经全面超越燃油车，只是有些人还不愿意承认。",
  "长期睡眠不足（每晚少于六小时）与记忆力下降、情绪波动存在明确关联，睡眠质量是影响日常认知功能的重要因素之一。",
  "数据显示城市咖啡馆密度与当地注册创业公司数量高度相关——说明好的创业生态，往往从一杯咖啡的社交文化开始。"
].map((statementText, index): MaterialItem => ({
  family_id: "practice",
  item_id: `P${index + 1}`,
  item_role: "filler",
  stance_version: "common",
  source_ids: [],
  topic_cluster: "practice",
  fallacy_type: "practice",
  argument_skeleton: "",
  statement_text: statementText,
  issue_note: "",
  char_count: statementText.length,
  read_seconds_target: 10,
  evidence_type: "",
  conclusion_strength: "",
  emotional_intensity: "",
  hostility_tone: "",
  familiarity: "",
  specificity: "",
  identity_label_flag: false,
  stats_flag: false,
  case_flag: false,
  mirror_match_flag: false,
  anchor_flag: false,
  anchor_type: "",
  needs_cross_rater: false
}));
const GENDER_OPTIONS = [
  { value: "", label: "请选择" },
  { value: "female", label: "女" },
  { value: "male", label: "男" },
  { value: "non_binary", label: "非二元/其他" },
  { value: "prefer_not_to_say", label: "不便回答" }
];

function parseLocalArray(key: string): unknown[] {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function getLocalCount(kind: string): number {
  return parseLocalArray(`socialgo:${kind}`).length;
}

function readHost(): string {
  return typeof window === "undefined" ? "unknown" : window.location.host;
}

function clampSeconds(value: number): number {
  if (!Number.isFinite(value)) {
    return 10;
  }
  return Math.min(60, Math.max(3, Math.round(value)));
}

function getDevSectionParts(blockType: "meta" | "non_meta" | "practice" | null): Screen[] {
  if (blockType === "meta") {
    return ["cue", "rating", "for_rating", "block_check"];
  }
  if (blockType === "non_meta") {
    return ["cue", "rating", "control_rating", "block_check"];
  }
  return ["cue", "rating", "block_check"];
}

export function App() {
  const [screen, setScreen] = useState<Screen>("intro");
  const [devMode, setDevMode] = useState(false);
  const [devTapCount, setDevTapCount] = useState(0);
  const [devUnlockOpen, setDevUnlockOpen] = useState(false);
  const [devPasswordInput, setDevPasswordInput] = useState("");
  const [devUnlockError, setDevUnlockError] = useState("");
  const [participantProfile, setParticipantProfile] = useState({
    participant_name: "",
    age: "",
    gender: "",
    consentAccepted: false
  });
  const [answers, setAnswers] = useState<AttitudeAnswer[]>(
    ATTITUDE_ITEMS.map((item) => ({ itemId: item.itemId, value: 4 }))
  );
  const [participant, setParticipant] = useState<ParticipantPayload | null>(null);
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [blockIndex, setBlockIndex] = useState(0);
  const [trialIndex, setTrialIndex] = useState(0);
  const [practiceIndex, setPracticeIndex] = useState(0);
  const [forScore, setForScore] = useState(4);
  const [persuasionScore, setPersuasionScore] = useState(4);
  const [pendingRatingData, setPendingRatingData] = useState<{
    attitudeScore: number;
    logicScore: number;
    ratingMs: number;
    shownAt: string;
  } | null>(null);
  const [controlResponse, setControlResponse] = useState("");
  const [completionCode, setCompletionCode] = useState("");
  const [forcedStance, setForcedStance] = useState<ParticipantStance | "auto">("auto");
  const [seedInput, setSeedInput] = useState("");
  const [questionLimitMs, setQuestionLimitMs] = useState(DEFAULT_QUESTION_LIMIT_MS);
  const [autoSubmitEnabled, setAutoSubmitEnabled] = useState(true);
  const [devHealth, setDevHealth] = useState<DevHealthState>({
    status: "idle",
    detail: "尚未检查 API / D1。"
  });
  const [devRuntimeMessage, setDevRuntimeMessage] = useState("本地回退尚未导出。");
  const [persistenceError, setPersistenceError] = useState<string | null>(null);
  const [readyCountdown, setReadyCountdown] = useState(READY_COUNTDOWN_S);
  const [blockCheckAnswers, setBlockCheckAnswers] = useState<Record<string, number>>({});
  const [blockRestSecondsLeft, setBlockRestSecondsLeft] = useState(BLOCK_REST_TOTAL_S);
  const [blockRestUnlocked, setBlockRestUnlocked] = useState(false);
  const [trialLog, setTrialLog] = useState<TrialLogEntry[]>([]);
  const trialShownAtRef = useRef(nowIso());
  const isSubmitting = useRef(false);

  useEffect(() => {
    if (screen !== "ready") return;
    setReadyCountdown(READY_COUNTDOWN_S);
    const id = setInterval(() => {
      setReadyCountdown((n) => {
        if (n <= 1) { clearInterval(id); return 0; }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [screen]);

  useEffect(() => {
    if (screen !== "block_check") return;
    setBlockRestSecondsLeft(BLOCK_REST_TOTAL_S);
    setBlockRestUnlocked(false);
    const id = setInterval(() => {
      setBlockRestSecondsLeft((n) => {
        const next = n - 1;
        if (next <= BLOCK_REST_TOTAL_S - BLOCK_REST_LOCK_S) setBlockRestUnlocked(true);
        if (next <= 0) { clearInterval(id); return 0; }
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [screen]);

  const currentBlock = assignment?.blocks[blockIndex] ?? null;
  const currentTrial = currentBlock?.trials[trialIndex] ?? null;
  const currentDevSectionParts = getDevSectionParts(currentBlock?.blockType ?? null);
  const currentDevPart = currentDevSectionParts.includes(screen) ? screen : currentDevSectionParts[0];
  const blockTrialTotal = currentBlock?.trials.length ?? 9;
  const blockTrialCurrent = Math.min(trialIndex + 1, blockTrialTotal);
  const currentPracticeItem = PRACTICE_ITEMS[practiceIndex] ?? null;
  const practiceCurrent = Math.min(practiceIndex + 1, PRACTICE_ITEMS.length);
  const roundTotal = assignment?.blocks.length ?? 4;
  const roundCurrent = Math.min(blockIndex + 1, roundTotal);
  const nextBlockType = assignment?.blocks[blockIndex + 1]?.blockType ?? null;
  const blockCheckItemKeys = currentBlock
    ? getBlockCheckItemKeys(currentBlock.blockType, nextBlockType)
    : [];
  const blockRestProgress = (BLOCK_REST_TOTAL_S - blockRestSecondsLeft) / BLOCK_REST_TOTAL_S;
  const isLastBlock = !nextBlockType;
  const currentSourceIds = currentTrial?.source_ids.join(", ") ?? "none";
  const localParticipantsCount = getLocalCount("participants");
  const localResponsesCount = getLocalCount("trial-responses");
  const localBlockChecksCount = getLocalCount("block-checks");
  const profileReady =
    participantProfile.participant_name.trim().length > 0 &&
    participantProfile.age.trim().length > 0 &&
    participantProfile.gender.trim().length > 0;

  const refreshTrialTimers = useCallback(() => {
    trialShownAtRef.current = nowIso();
  }, []);

  const blockDevWrite = useCallback((message: string): PersistenceOutcome => {
    setPersistenceError(null);
    setDevRuntimeMessage(message);
    return "blocked";
  }, []);

  const failPersistence = useCallback((message: string): PersistenceOutcome => {
    setPersistenceError(message);
    return "failed";
  }, []);

  const persistParticipant = useCallback(
    async (payload: ParticipantPayload): Promise<PersistenceOutcome> => {
      if (devMode) {
        return blockDevWrite("开发者模式下不会写入参与者数据。");
      }

      try {
        await saveParticipant(payload);
        setPersistenceError(null);
        return "saved";
      } catch {
        return failPersistence("参与者信息保存失败。请先不要刷新页面，并联系研究者。");
      }
    },
    [blockDevWrite, devMode, failPersistence]
  );

  const persistTrialResponse = useCallback(
    async (payload: TrialResponsePayload): Promise<PersistenceOutcome> => {
      if (devMode) {
        return blockDevWrite("开发者模式下不会写入题目作答数据。");
      }

      try {
        await saveTrialResponse(payload);
        setPersistenceError(null);
        return "saved";
      } catch {
        return failPersistence("题目作答保存失败。请先不要刷新页面，并联系研究者。");
      }
    },
    [blockDevWrite, devMode, failPersistence]
  );

  const persistBlockCheck = useCallback(
    async (payload: BlockCheckPayload): Promise<PersistenceOutcome> => {
      if (devMode) {
        return blockDevWrite("开发者模式下不会写入区块回顾数据。");
      }

      try {
        await saveBlockCheck(payload);
        setPersistenceError(null);
        return "saved";
      } catch {
        return failPersistence("区块回顾保存失败。请先不要刷新页面，并联系研究者。");
      }
    },
    [blockDevWrite, devMode, failPersistence]
  );

  const finalizeParticipant = useCallback(async (): Promise<{ outcome: PersistenceOutcome; completionCode: string }> => {
    if (!participant) {
      return {
        outcome: "failed",
        completionCode: ""
      };
    }

    if (devMode) {
      return {
        outcome: blockDevWrite("开发者模式下不会写入完成状态。"),
        completionCode: "DEV-MODE"
      };
    }

    try {
      const code = await completeParticipant(participant.participant_id);
      setPersistenceError(null);
      return {
        outcome: "saved",
        completionCode: code
      };
    } catch {
      return {
        outcome: failPersistence("完成状态保存失败。请先不要关闭页面，并联系研究者。"),
        completionCode: ""
      };
    }
  }, [blockDevWrite, devMode, failPersistence, participant]);

  const setAnswer = useCallback((itemId: string, value: number) => {
    setAnswers((current) =>
      current.map((answer) => (answer.itemId === itemId ? { ...answer, value } : answer))
    );
  }, []);

  const updateProfile = useCallback(
    (field: "participant_name" | "age" | "gender" | "consentAccepted", value: string | boolean) => {
      setParticipantProfile((current) => ({
        ...current,
        [field]: value
      }));
    },
    []
  );

  const resolveStance = useCallback((): ParticipantStance => {
    if (forcedStance !== "auto") {
      return forcedStance;
    }
    return participant?.stance ?? scoreAttitude(answers, seedInput.trim()).stance;
  }, [answers, forcedStance, participant, seedInput]);

  const createOrResetAssignment = useCallback(
    (nextSeed?: string) => {
      const resolvedSeed = (nextSeed ?? seedInput).trim() || createId("seed");
      const resolvedStance = resolveStance();
      const nextAssignment = createAssignment(MATERIALS.items, resolvedStance, resolvedSeed);

      setSeedInput(resolvedSeed);
      setAssignment(nextAssignment);
      setBlockIndex(0);
      setTrialIndex(0);
      setCompletionCode("");
      setScreen("cue");
      setParticipant((current) =>
        current
          ? {
              ...current,
              stance: resolvedStance,
              assignment_seed: resolvedSeed
            }
          : current
      );
    },
    [resolveStance, seedInput]
  );

  const ensureDevJumpState = useCallback(
    (nextScreen: Screen) => {
      const needsParticipant = ["practice_intro", "practice"].includes(nextScreen);
      const needsAssignment = ["cue", "rating", "for_rating", "control_rating", "block_check", "done"].includes(nextScreen);
      const seed = seedInput.trim() || participant?.assignment_seed || createId("seed");
      const scored = scoreAttitude(answers, seed);
      const stance = forcedStance === "auto" ? participant?.stance ?? scored.stance : forcedStance;

      if (!participantProfile.participant_name.trim() || !participantProfile.age.trim() || !participantProfile.gender.trim()) {
        setParticipantProfile((current) => ({
          participant_name: current.participant_name.trim() || "测试参与者",
          age: current.age.trim() || "24",
          gender: current.gender || "female",
          consentAccepted: true
        }));
      }

      let nextParticipant = participant;
      if (needsParticipant && !nextParticipant) {
        const createdAt = nowIso();
        nextParticipant = {
          participant_id: createId("p"),
          created_at: createdAt,
          consent_at: createdAt,
          participant_name: participantProfile.participant_name.trim() || "测试参与者",
          age: Number(participantProfile.age) || 24,
          gender: participantProfile.gender || "female",
          stance,
          intensity: scored.intensity,
          attitude_scale_payload: answers,
          assignment_seed: seed,
          study_version: STUDY_VERSION
        };
        setParticipant(nextParticipant);
      }

      if (needsAssignment && !assignment) {
        setAssignment(createAssignment(MATERIALS.items, stance, seed));
        setSeedInput(seed);
        setBlockIndex(0);
        setTrialIndex(0);
        setParticipant((current) =>
          current
            ? {
                ...current,
                stance,
                assignment_seed: seed
              }
            : current
        );
      }

      if (nextScreen === "practice") {
        setPracticeIndex(0);
        refreshTrialTimers();
      }

      if (["cue", "rating", "for_rating", "control_rating", "block_check"].includes(nextScreen)) {
        refreshTrialTimers();
      }

      if ((nextScreen === "for_rating" || nextScreen === "control_rating") && !pendingRatingData) {
        setPendingRatingData({
          attitudeScore: 4,
          logicScore: 4,
          ratingMs: 1200,
          shownAt: nowIso()
        });
      }

      if (nextScreen === "done" && !completionCode) {
        setCompletionCode(`DEV-${seed.slice(-6).toUpperCase()}`);
      }
    },
    [
      answers,
      assignment,
      completionCode,
      forcedStance,
      participant,
      participantProfile.age,
      participantProfile.gender,
      participantProfile.participant_name,
      pendingRatingData,
      refreshTrialTimers,
      seedInput
    ]
  );

  const jumpToScreen = useCallback(
    (nextScreen: Screen) => {
      if (devMode) {
        ensureDevJumpState(nextScreen);
        setScreen(nextScreen);
        return;
      }

      if (["cue", "rating", "for_rating", "control_rating", "block_check", "done"].includes(nextScreen) && !assignment) {
        return;
      }

      if ((nextScreen === "practice_intro" || nextScreen === "practice") && !participant) {
        return;
      }

      if ((nextScreen === "rating" && currentTrial) || nextScreen === "practice") {
        refreshTrialTimers();
      }

      setScreen(nextScreen);
    },
    [assignment, currentTrial, devMode, ensureDevJumpState, participant, refreshTrialTimers]
  );

  const jumpToTrialPosition = useCallback(
    (nextBlockIndex: number, nextTrialIndex: number, nextScreen: Screen = "rating") => {
      if (!assignment) {
        return;
      }

      const block = assignment.blocks[nextBlockIndex];
      if (!block || !block.trials[nextTrialIndex]) {
        return;
      }

      setBlockIndex(nextBlockIndex);
      setTrialIndex(nextTrialIndex);
      refreshTrialTimers();
      setScreen(nextScreen);
    },
    [assignment, refreshTrialTimers]
  );

  const jumpToSectionPart = useCallback(
    (nextBlockIndex: number, nextTrialIndex: number, nextScreen: Screen) => {
      if (!assignment) {
        return;
      }

      const block = assignment.blocks[nextBlockIndex];
      if (!block) {
        return;
      }

      const allowedParts = getDevSectionParts(block.blockType);
      const resolvedScreen = allowedParts.includes(nextScreen) ? nextScreen : allowedParts[0];
      const resolvedTrialIndex = block.trials[nextTrialIndex] ? nextTrialIndex : 0;

      if (devMode) {
        ensureDevJumpState(resolvedScreen);
      }
      jumpToTrialPosition(nextBlockIndex, resolvedTrialIndex, resolvedScreen);
    },
    [assignment, devMode, ensureDevJumpState, jumpToTrialPosition]
  );

  const jumpRelativeTrial = useCallback(
    (direction: -1 | 1) => {
      if (!assignment) {
        return;
      }

      let nextBlockIndex = blockIndex;
      let nextTrialIndex = trialIndex + direction;

      if (direction === 1 && nextTrialIndex >= (assignment.blocks[nextBlockIndex]?.trials.length ?? 0)) {
        nextBlockIndex += 1;
        nextTrialIndex = 0;
      }

      if (direction === -1 && nextTrialIndex < 0) {
        nextBlockIndex -= 1;
        nextTrialIndex = assignment.blocks[nextBlockIndex]?.trials.length
          ? assignment.blocks[nextBlockIndex].trials.length - 1
          : -1;
      }

      jumpToSectionPart(nextBlockIndex, nextTrialIndex, currentDevPart);
    },
    [assignment, blockIndex, currentDevPart, jumpToSectionPart, trialIndex]
  );

  const handleDevDotClick = useCallback(() => {
    setDevTapCount((current) => {
      const next = current + 1;
      if (next < 3) {
        return next;
      }

      setDevPasswordInput("");
      setDevUnlockError("");
      setDevUnlockOpen(true);
      return 0;
    });
  }, []);

  const submitDevUnlock = useCallback(() => {
    if (devPasswordInput.trim() === "socialgo") {
      setDevMode(true);
      setDevUnlockOpen(false);
      setDevPasswordInput("");
      setDevUnlockError("");
      return;
    }

    setDevUnlockError("口令不正确。");
  }, [devPasswordInput]);

  const fillTestProfile = useCallback(() => {
    setParticipantProfile({
      participant_name: "测试参与者",
      age: "24",
      gender: "female",
      consentAccepted: true
    });
  }, []);

  const clearSession = useCallback(() => {
    setParticipant(null);
    setAssignment(null);
    setBlockIndex(0);
    setTrialIndex(0);
    setPracticeIndex(0);
    setForScore(4);
    setPersuasionScore(4);
    setControlResponse("");
    setCompletionCode("");
    setTrialLog([]);
    setScreen("profile");
  }, []);

  const startStudy = useCallback(async () => {
    if (!profileReady || !participantProfile.consentAccepted) return;
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    try {
      const participantId = createId("p");
      const seed = seedInput.trim() || createId("seed");
      const scored = scoreAttitude(answers, seed);
      const createdAt = nowIso();
      const stance = forcedStance === "auto" ? scored.stance : forcedStance;
      const payload: ParticipantPayload = {
        participant_id: participantId,
        created_at: createdAt,
        consent_at: createdAt,
        participant_name: participantProfile.participant_name.trim(),
        age: Number(participantProfile.age),
        gender: participantProfile.gender,
        stance,
        intensity: scored.intensity,
        attitude_scale_payload: answers,
        assignment_seed: seed,
        study_version: STUDY_VERSION
      };

      const nextAssignment = createAssignment(MATERIALS.items, stance, seed);
      setSeedInput(seed);
      setParticipant(payload);
      setAssignment(nextAssignment);
      setBlockIndex(0);
      setTrialIndex(0);
      setPracticeIndex(0);
      const saveOutcome = await persistParticipant(payload);
      if (saveOutcome === "failed") return;
      setScreen("practice_intro");
    } finally {
      isSubmitting.current = false;
    }
  }, [answers, forcedStance, participantProfile, persistParticipant, profileReady, seedInput]);

  const beginPractice = useCallback(() => {
    setPracticeIndex(0);
    refreshTrialTimers();
    setScreen("practice");
  }, [refreshTrialTimers]);

  const beginFormalTask = useCallback(() => {
    refreshTrialTimers();
    setScreen("cue");
  }, [refreshTrialTimers]);

  const submitPracticeRating = useCallback(
    async ({ attitudeScore, logicScore, ratingMs }: { attitudeScore: number; logicScore: number; ratingMs: number }) => {
      if (!participant || !currentPracticeItem) return;
      if (isSubmitting.current) return;
      isSubmitting.current = true;
      try {
        const saveOutcome = await persistTrialResponse({
          response_id: createId("r"),
          participant_id: participant.participant_id,
          block_index: PRACTICE_BLOCK_INDEX,
          block_type: "practice",
          trial_index: practiceIndex,
          family_id: currentPracticeItem.family_id,
          item_id: currentPracticeItem.item_id,
          item_role: currentPracticeItem.item_role,
          stance_version: currentPracticeItem.stance_version,
          fallacy_type: currentPracticeItem.fallacy_type,
          statement_text_snapshot: currentPracticeItem.statement_text,
          attitude_score: attitudeScore,
          logic_score: logicScore,
          detected_target_fallacy: provisionalDetectedTargetFallacy(logicScore),
          logic_defect_level_0_3: null,
          for_score: null,
          persuasion_score: null,
          rt_ms: ratingMs,
          shown_at: trialShownAtRef.current,
          answered_at: nowIso()
        });
        if (saveOutcome === "failed") return;

        if (practiceIndex + 1 < PRACTICE_ITEMS.length) {
          setPracticeIndex((current) => current + 1);
          refreshTrialTimers();
          return;
        }

        beginFormalTask();
      } finally {
        isSubmitting.current = false;
      }
    },
    [beginFormalTask, currentPracticeItem, participant, persistTrialResponse, practiceIndex, refreshTrialTimers]
  );

  const beginTrial = useCallback(() => {
    refreshTrialTimers();
    setScreen("rating");
  }, [refreshTrialTimers]);

  const rerollCurrentTrial = useCallback(() => {
    if (!assignment || !currentTrial) {
      return;
    }

    const replacementPool = MATERIALS.items.filter((item) => {
      if (item.item_id === currentTrial.item_id) {
        return false;
      }
      if (item.item_role !== currentTrial.item_role) {
        return false;
      }
      if (currentTrial.item_role === "key") {
        return item.stance_version === currentTrial.stance_version;
      }
      return true;
    });

    if (replacementPool.length === 0) {
      return;
    }

    const replacement = replacementPool[Math.floor(Math.random() * replacementPool.length)];
    setAssignment((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        blocks: current.blocks.map((block) =>
          block.blockIndex !== blockIndex
            ? block
            : {
                ...block,
                trials: block.trials.map((trial, index) => (index === trialIndex ? replacement : trial))
              }
        )
      };
    });
    refreshTrialTimers();
  }, [assignment, blockIndex, currentTrial, refreshTrialTimers, trialIndex]);

  const submitRating = useCallback(
    async ({ attitudeScore, logicScore, ratingMs }: { attitudeScore: number; logicScore: number; ratingMs: number }) => {
      if (!participant || !currentBlock || !currentTrial) {
        return;
      }

      // Meta blocks: hold the rating until FOR is answered, then save together
      if (currentBlock.blockType === "meta") {
        setPendingRatingData({ attitudeScore, logicScore, ratingMs, shownAt: trialShownAtRef.current });
        setForScore(4);
        setScreen("for_rating");
        return;
      }

      // Non-meta: hold rating until persuasion question is answered
      setPendingRatingData({ attitudeScore, logicScore, ratingMs, shownAt: trialShownAtRef.current });
      setPersuasionScore(4);
      setScreen("control_rating");
    },
    [currentBlock, currentTrial, participant, refreshTrialTimers, trialIndex]
  );

  const submitForRating = useCallback(async () => {
    if (!participant || !currentBlock || !currentTrial || !pendingRatingData) return;
    if (currentBlock.blockType === "practice") return;
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    try {
      const saveOutcome = await persistTrialResponse({
        response_id: createId("r"),
        participant_id: participant.participant_id,
        block_index: currentBlock.blockIndex,
        block_type: currentBlock.blockType,
        trial_index: trialIndex,
        family_id: currentTrial.family_id,
        item_id: currentTrial.item_id,
        item_role: currentTrial.item_role,
        stance_version: currentTrial.stance_version,
        fallacy_type: currentTrial.fallacy_type,
        statement_text_snapshot: currentTrial.statement_text,
        attitude_score: pendingRatingData.attitudeScore,
        logic_score: pendingRatingData.logicScore,
        detected_target_fallacy: provisionalDetectedTargetFallacy(pendingRatingData.logicScore),
        logic_defect_level_0_3: null,
        for_score: forScore,
        persuasion_score: null,
        rt_ms: pendingRatingData.ratingMs,
        shown_at: pendingRatingData.shownAt,
        answered_at: nowIso()
      });
      if (saveOutcome === "failed") return;

      setPendingRatingData(null);
      setTrialLog((prev) => [
        ...prev,
        {
          blockIndex: currentBlock.blockIndex,
          blockType: currentBlock.blockType as "meta" | "non_meta",
          attitudeScore: pendingRatingData.attitudeScore,
          logicScore: pendingRatingData.logicScore,
          forScore,
          rtMs: pendingRatingData.ratingMs,
          itemRole: currentTrial.item_role,
          stanceVersion: currentTrial.stance_version,
        },
      ]);

      if (trialIndex + 1 < currentBlock.trials.length) {
        setTrialIndex((current) => current + 1);
        refreshTrialTimers();
        setScreen("rating");
        return;
      }

      setScreen("block_check");
    } finally {
      isSubmitting.current = false;
    }
  }, [currentBlock, currentTrial, forScore, participant, pendingRatingData, persistTrialResponse, refreshTrialTimers, trialIndex]);

  const submitControlRating = useCallback(async () => {
    if (!participant || !currentBlock || !currentTrial || !pendingRatingData) return;
    if (currentBlock.blockType === "practice") return;
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    try {
      const saveOutcome = await persistTrialResponse({
        response_id: createId("r"),
        participant_id: participant.participant_id,
        block_index: currentBlock.blockIndex,
        block_type: currentBlock.blockType,
        trial_index: trialIndex,
        family_id: currentTrial.family_id,
        item_id: currentTrial.item_id,
        item_role: currentTrial.item_role,
        stance_version: currentTrial.stance_version,
        fallacy_type: currentTrial.fallacy_type,
        statement_text_snapshot: currentTrial.statement_text,
        attitude_score: pendingRatingData.attitudeScore,
        logic_score: pendingRatingData.logicScore,
        detected_target_fallacy: provisionalDetectedTargetFallacy(pendingRatingData.logicScore),
        logic_defect_level_0_3: null,
        for_score: null,
        persuasion_score: persuasionScore,
        rt_ms: pendingRatingData.ratingMs,
        shown_at: pendingRatingData.shownAt,
        answered_at: nowIso()
      });
      if (saveOutcome === "failed") return;

      setPendingRatingData(null);
      setTrialLog((prev) => [
        ...prev,
        {
          blockIndex: currentBlock.blockIndex,
          blockType: currentBlock.blockType as "meta" | "non_meta",
          attitudeScore: pendingRatingData.attitudeScore,
          logicScore: pendingRatingData.logicScore,
          forScore: null,
          rtMs: pendingRatingData.ratingMs,
          itemRole: currentTrial.item_role,
          stanceVersion: currentTrial.stance_version,
        },
      ]);

      if (trialIndex + 1 < currentBlock.trials.length) {
        setTrialIndex((current) => current + 1);
        refreshTrialTimers();
        setScreen("rating");
        return;
      }

      setScreen("block_check");
    } finally {
      isSubmitting.current = false;
    }
  }, [currentBlock, currentTrial, participant, pendingRatingData, persuasionScore, persistTrialResponse, refreshTrialTimers, trialIndex]);

  const saveDebugResponse = useCallback(() => {
    setDevRuntimeMessage("开发者模式下已禁用测试写入。");
  }, []);

  const previewMockResults = useCallback(() => {
    const createdAt = nowIso();
    setParticipant({
      participant_id: "mock-preview",
      created_at: createdAt,
      consent_at: createdAt,
      participant_name: "Mock 参与者",
      age: 22,
      gender: "female",
      stance: MOCK_PARTICIPANT_STANCE,
      intensity: MOCK_PARTICIPANT_INTENSITY,
      attitude_scale_payload: [],
      assignment_seed: "mock-seed",
      study_version: STUDY_VERSION,
    });
    setTrialLog(MOCK_TRIAL_LOG);
    setCompletionCode("MOCK-PREVIEW");
    setScreen("done");
  }, []);

  const submitBlockCheck = useCallback(async () => {
    if (!participant || !currentBlock) return;
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    try {
      const nextBlockType = assignment?.blocks[blockIndex + 1]?.blockType ?? null;
      const itemKeys = getBlockCheckItemKeys(currentBlock.blockType, nextBlockType);
      const answers = itemKeys.length > 0
        ? Object.fromEntries(itemKeys.map((k) => [k, blockCheckAnswers[k] ?? 3]))
        : null;

      const payload: BlockCheckPayload = {
        check_id: createId("bc"),
        participant_id: participant.participant_id,
        block_index: currentBlock.blockIndex,
        block_type: currentBlock.blockType,
        for_score: null,
        control_response: null,
        inter_block_answers: answers
      };

      const saveOutcome = await persistBlockCheck(payload);
      if (saveOutcome === "failed") return;
      setForScore(4);
      setBlockCheckAnswers({});

      if (assignment && blockIndex + 1 < assignment.blocks.length) {
        setBlockIndex((current) => current + 1);
        setTrialIndex(0);
        setScreen("cue");
        return;
      }

      const completion = await finalizeParticipant();
      if (completion.outcome === "failed") return;
      setCompletionCode(completion.completionCode);
      setScreen("done");
    } finally {
      isSubmitting.current = false;
    }
  }, [assignment, blockCheckAnswers, blockIndex, currentBlock, finalizeParticipant, participant, persistBlockCheck]);

  const checkApiHealth = useCallback(async () => {
    setDevHealth({ status: "checking", detail: "正在检查 API / D1..." });

    try {
      const response = await fetch("/api/health");
      if (!response.ok) {
        setDevHealth({
          status: "error",
          detail: `接口返回 ${response.status}。当前大概率处于本地 fallback。`
        });
        return;
      }

      const payload = (await response.json()) as {
        ok?: boolean;
        study_version?: string;
        participants?: number;
        trial_responses?: number;
      };
      setDevHealth({
        status: "ok",
        detail: `D1 可用。participants=${payload.participants ?? 0}，trial_responses=${payload.trial_responses ?? 0}，version=${payload.study_version ?? "unknown"}`
      });
    } catch {
      setDevHealth({
        status: "error",
        detail: "无法访问 /api/health。当前本地 Vite 环境会走 localStorage 回退。"
      });
    }
  }, []);

  const exportLocalFallback = useCallback(async () => {
    const snapshot = {
      exported_at: nowIso(),
      participants: parseLocalArray("socialgo:participants"),
      trial_responses: parseLocalArray("socialgo:trial-responses"),
      block_checks: parseLocalArray("socialgo:block-checks")
    };
    const serialized = JSON.stringify(snapshot, null, 2);

    try {
      await navigator.clipboard.writeText(serialized);
      setDevRuntimeMessage("本地回退 JSON 已复制到剪贴板。");
    } catch {
      setDevRuntimeMessage("复制失败，请在控制台读取 localStorage。");
    }
  }, []);

  const selectedQuestionSeconds = Math.round(questionLimitMs / 1000);

  return (
    <main className="app-shell">
      <section className="workspace">
        {devMode ? (
          <section className="dev-flowbar">
            {FLOW_STEPS.map((step) => (
              <button
                key={step.id}
                type="button"
                className={step.id === screen ? "active" : ""}
                onClick={() => jumpToScreen(step.id)}
              >
                {step.label}
              </button>
            ))}
          </section>
        ) : null}

        {devMode ? (
          <p className="persistence-banner warning">开发者模式已开启。当前页面内的实验数据不会写入正式数据库。</p>
        ) : null}

        {persistenceError ? (
          <p className="persistence-banner error">{persistenceError}</p>
        ) : null}

        {screen === "rating" ? (
          <ProgressRail
            current={blockTrialCurrent}
            total={blockTrialTotal}
            label="本部分"
            variant="capsules"
          />
        ) : null}

        {screen === "intro" ? (
          <section className="intro-shell">
            <div className="intro-hero">
              <p className="eyebrow">SocialGo Research</p>
              <h1>
                性别议题下
                <br />
                社会观点阅读实验
              </h1>
              <p className="lead">
                你将阅读一系列关于性别议题的公共讨论中的短陈述，并对它们做出快速判断。整个流程包含基本信息填写、知情同意、若干评分环节和结束页。
              </p>
              <div className="intro-meta">
                <div className="meta-item">
                  <strong>预计时长</strong>
                  <span>15 - 20 分钟</span>
                </div>
                <div className="meta-item">
                  <strong>环境要求</strong>
                  <span>安静、无打扰</span>
                </div>
              </div>
            </div>
            <div className="intro-card">
              <h2>实验流程</h2>
              <div className="flow-steps">
                <div className="step-item">
                  <div className="step-num">01</div>
                  <div className="step-text">
                    <strong>基本信息与同意书</strong>
                    <p>填写基础信息并了解研究知情同意</p>
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-num">02</div>
                  <div className="step-text">
                    <strong>态度问卷</strong>
                    <p>回答几个关于特定话题的简单问题</p>
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-num">03</div>
                  <div className="step-text">
                    <strong>阅读与双维评分</strong>
                    <p>浏览社交媒体短文本，并在二维面板上评估“认同度”与“逻辑性”</p>
                  </div>
                </div>
              </div>
              <button className="sharp-button" type="button" onClick={() => setScreen("profile")}>
                开始实验 →
              </button>
            </div>
          </section>
        ) : null}

        {screen === "profile" ? (
          <section className="flow-panel form-shell">
            <button className="dev-dot" type="button" aria-label="Developer mode" onClick={handleDevDotClick} />
            <p className="eyebrow">开始前</p>
            <h2>参与者信息</h2>
            <p className="lead">请先填写基本信息。该信息仅用于研究记录与后续数据整理。</p>
            <div className="profile-grid">
              <label className="field">
                <span>姓名</span>
                <input
                  type="text"
                  value={participantProfile.participant_name}
                  onChange={(event) => updateProfile("participant_name", event.target.value)}
                  placeholder="请输入姓名"
                />
              </label>
              <label className="field">
                <span>年龄</span>
                <input
                  type="number"
                  min="1"
                  max="120"
                  value={participantProfile.age}
                  onChange={(event) => updateProfile("age", event.target.value)}
                  placeholder="请输入年龄"
                />
              </label>
              <label className="field">
                <span>性别</span>
                <select
                  value={participantProfile.gender}
                  onChange={(event) => updateProfile("gender", event.target.value)}
                >
                  {GENDER_OPTIONS.map((option) => (
                    <option key={option.value || "empty"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="form-actions">
              <button className="secondary-button" type="button" onClick={() => setScreen("intro")}>
                返回
              </button>
              <button
                className="primary-button"
                type="button"
                onClick={() => setScreen("consent")}
                disabled={!profileReady}
              >
                下一步
              </button>
            </div>
          </section>
        ) : null}

        {screen === "consent" ? (
          <section className="flow-panel scale-stack">
            <p className="eyebrow">开始前</p>
            <h2>知情同意书</h2>
            <section className="consent-sheet">
              <h3>参与说明</h3>
              <p>
                你将参与一项关于社会观点阅读与判断的在线研究。研究过程中会记录你的基础信息、问卷反应与任务评分结果，用于后续学术分析。
              </p>
              <p>
                参与完全自愿。你可以在过程中随时停止，不会产生额外后果。除研究分析所需的信息外，本研究不会要求你提交额外敏感资料。
              </p>
              <p>
                如果你同意参与，请勾选下方确认项并继续。若你不同意，请不要进入后续任务。
              </p>
              <label className="consent-check">
                <input
                  type="checkbox"
                  checked={participantProfile.consentAccepted}
                  onChange={(event) => updateProfile("consentAccepted", event.target.checked)}
                />
                <span>我已阅读并同意以上说明，自愿参与本研究。</span>
              </label>
            </section>
            <div className="form-actions">
              <button className="secondary-button" type="button" onClick={() => setScreen("profile")}>
                返回
              </button>
              <button
                className="primary-button"
                type="button"
                onClick={() => setScreen("ready")}
                disabled={!participantProfile.consentAccepted}
              >
                继续
              </button>
            </div>
          </section>
        ) : null}

        {screen === "ready" ? (
          <section className="flow-panel ready-shell">
            <p className="eyebrow">开始前</p>
            <h2>准备进入实验</h2>
            <p className="lead">请在继续之前确认以下各项已就绪。</p>
            <ul className="ready-checklist">
              <li className="ready-item">
                <span className="ready-icon" aria-hidden="true">🔕</span>
                <div>
                  <strong>将手机及其他设备调至静音或勿扰模式</strong>
                  <p>实验期间的通知和声音会干扰你的判断。</p>
                </div>
              </li>
              <li className="ready-item">
                <span className="ready-icon" aria-hidden="true">🚪</span>
                <div>
                  <strong>确保所在环境在接下来 15–20 分钟内不会被打扰</strong>
                  <p>告知周围的人你正在进行实验，暂时不便回应。</p>
                </div>
              </li>
              <li className="ready-item">
                <span className="ready-icon" aria-hidden="true">📖</span>
                <div>
                  <strong>实验过程中请认真阅读每一条材料</strong>
                  <p>每道题都有时间限制，请保持专注，根据第一反应作答。</p>
                </div>
              </li>
              <li className="ready-item">
                <span className="ready-icon" aria-hidden="true">🖥️</span>
                <div>
                  <strong>保持本页面全屏，不要切换其他应用</strong>
                  <p>中途离开屏幕可能影响计时数据的有效性。</p>
                </div>
              </li>
            </ul>
            <div className="ready-actions">
              <button className="secondary-button" type="button" onClick={() => setScreen("consent")}>
                返回
              </button>
              <button
                className={`danger-button${readyCountdown > 0 ? " counting" : ""}`}
                type="button"
                disabled={readyCountdown > 0}
                onClick={() => setScreen("attitude")}
              >
                {readyCountdown > 0 ? `请阅读上方内容（${readyCountdown}）` : "我已准备好，开始实验 →"}
              </button>
            </div>
          </section>
        ) : null}

        {screen === "attitude" ? (
          <section className="flow-panel scale-stack">
            <h2>态度小问卷</h2>
            {ATTITUDE_ITEMS.map((item) => {
              const value = answers.find((answer) => answer.itemId === item.itemId)?.value ?? 4;
              return (
                <ScaleQuestion
                  key={item.itemId}
                  label={item.label}
                  value={value}
                  lowLabel={item.lowLabel}
                  highLabel={item.highLabel}
                  onChange={(nextValue) => setAnswer(item.itemId, nextValue)}
                />
              );
            })}
            <div className="form-actions">
              <button className="secondary-button" type="button" onClick={() => setScreen("consent")}>
                返回
              </button>
              <button className="primary-button" type="button" onClick={startStudy}>
                继续
              </button>
            </div>
          </section>
        ) : null}

        {screen === "practice_intro" ? (
          <section className="flow-panel">
            <ProgressRail
              current={0}
              total={roundTotal}
              label="Section"
              variant="capsules"
              showSessionMarker
              valueLabel="练习"
            />
            <h2>二维打分练习</h2>
            <p className="lead">
              接下来有 5 道练习题。请阅读每一句陈述，依次判断你的认同程度，以及论述是否合理。
            </p>
            <div className="form-actions">
              <button className="secondary-button" type="button" onClick={() => setScreen("attitude")}>
                返回
              </button>
              <button className="primary-button" type="button" onClick={beginPractice}>
                开始练习
              </button>
            </div>
          </section>
        ) : null}

        {screen === "practice" && currentPracticeItem ? (
          <section className="trial-layout">
            <ProgressRail
              current={practiceCurrent}
              total={PRACTICE_ITEMS.length}
              label="题目"
              variant="capsules"
            />
            {autoSubmitEnabled ? (
              <TimeRail key={`practice-time-${practiceIndex}-${questionLimitMs}`} durationMs={questionLimitMs} />
            ) : (
              <p className="dev-inline-hint">开发者模式下已关闭自动提交，需要手动点击提交。</p>
            )}
            <StatementCard key={currentPracticeItem.item_id} item={currentPracticeItem} />
            <RatingPad
              key={`practice-${practiceIndex}-${questionLimitMs}-${autoSubmitEnabled ? "auto" : "manual"}`}
              durationMs={questionLimitMs}
              autoSubmitEnabled={autoSubmitEnabled}
              onSubmit={submitPracticeRating}
            />
          </section>
        ) : null}

        {screen === "cue" && currentBlock ? (
          <section className="flow-panel">
            <ProgressRail
              current={roundCurrent}
              total={roundTotal}
              label="Section"
              variant="capsules"
              showSessionMarker
              sessionMarkerComplete
            />
            <p className="eyebrow">第 {currentBlock.blockIndex + 1} 部分</p>
            <h2>准备开始</h2>
            <p className="lead">接下来请依次阅读一组陈述，并完成评分。</p>
            <button className="primary-button" type="button" onClick={beginTrial}>
              开始
            </button>
          </section>
        ) : null}

        {screen === "rating" && currentBlock && currentTrial ? (
          <section className="trial-layout">
            {autoSubmitEnabled ? (
              <TimeRail key={`time-${currentBlock.blockIndex}-${trialIndex}-${questionLimitMs}`} durationMs={questionLimitMs} />
            ) : (
              <p className="dev-inline-hint">开发者模式下已关闭自动提交，需要手动点击提交。</p>
            )}
            <StatementCard key={currentTrial.item_id} item={currentTrial} />
            <RatingPad
              key={`${currentBlock.blockIndex}-${trialIndex}-${questionLimitMs}-${autoSubmitEnabled ? "auto" : "manual"}`}
              durationMs={questionLimitMs}
              autoSubmitEnabled={autoSubmitEnabled}
              onSubmit={submitRating}
            />
          </section>
        ) : null}

        {screen === "for_rating" && currentBlock && currentTrial && pendingRatingData ? (
          <section className="trial-layout">
            <div className="for-rating-shell">
              <div className="for-rating-frozen-col">
                <p className="for-rating-col-label">刚才的判断</p>
                <FrozenRatingPad
                  attitudeScore={pendingRatingData.attitudeScore}
                  logicScore={pendingRatingData.logicScore}
                />
                <div className="for-rating-readout">
                  <span>认同度 <strong>{pendingRatingData.attitudeScore}</strong>/5</span>
                  <span>论证合理程度 <strong>{pendingRatingData.logicScore}</strong>/5</span>
                </div>
              </div>
              <div className="for-rating-question-col">
                <ScaleQuestion
                  label="回顾刚才的判断，我感觉是正确的。"
                  value={forScore}
                  lowLabel="完全不同意"
                  highLabel="完全同意"
                  onChange={setForScore}
                />
                <button className="primary-button" type="button" onClick={submitForRating}>
                  继续
                </button>
              </div>
            </div>
          </section>
        ) : null}

        {screen === "control_rating" && currentBlock && currentTrial && pendingRatingData ? (
          <section className="trial-layout">
            <div className="for-rating-shell">
              <div className="for-rating-frozen-col">
                <p className="for-rating-col-label">刚才的判断</p>
                <FrozenRatingPad
                  attitudeScore={pendingRatingData.attitudeScore}
                  logicScore={pendingRatingData.logicScore}
                />
                <div className="for-rating-readout">
                  <span>认同度 <strong>{pendingRatingData.attitudeScore}</strong>/5</span>
                  <span>论证合理程度 <strong>{pendingRatingData.logicScore}</strong>/5</span>
                </div>
              </div>
              <div className="for-rating-question-col">
                <ScaleQuestion
                  label="这个说法对一般人的说服力有多强？"
                  value={persuasionScore}
                  lowLabel="完全没有说服力"
                  highLabel="非常有说服力"
                  onChange={setPersuasionScore}
                />
                <button className="primary-button" type="button" onClick={submitControlRating}>
                  继续
                </button>
              </div>
            </div>
          </section>
        ) : null}

        {screen === "block_check" && currentBlock ? (
          <section className="flow-panel block-check-shell">
            <p className="eyebrow">第 {currentBlock.blockIndex + 1} 部分完成</p>
            <h2>本轮已完成</h2>

            {isLastBlock ? (
              <p className="lead">所有部分已完成，点击继续查看结果。</p>
            ) : (
              <>
                <p className="lead">请回答以下问题，然后稍作休息。</p>

                <div className="block-check-scales">
                  {blockCheckItemKeys.map((key) => {
                    const item = BLOCK_CHECK_ITEMS[key];
                    return (
                      <ScaleQuestion
                        key={key}
                        label={item.label}
                        value={blockCheckAnswers[key] ?? 3}
                        lowLabel={item.lowLabel}
                        highLabel={item.highLabel}
                        onChange={(v) => setBlockCheckAnswers((prev) => ({ ...prev, [key]: v }))}
                      />
                    );
                  })}
                </div>

                <div className="rest-section">
                  <div className="rest-rail">
                    <div
                      className={`rest-rail-fill${blockRestUnlocked ? " is-unlocked" : ""}`}
                      style={{ transform: `scaleX(${blockRestProgress})` }}
                    />
                    <div className="rest-rail-threshold" />
                  </div>
                  <p className="rest-status">
                    {blockRestUnlocked
                      ? "休息完毕，可以继续了。"
                      : `休息中，还需 ${blockRestSecondsLeft - (BLOCK_REST_TOTAL_S - BLOCK_REST_LOCK_S)} 秒后可继续…`}
                  </p>
                </div>
              </>
            )}

            <button
              className="primary-button"
              type="button"
              disabled={!isLastBlock && !blockRestUnlocked}
              onClick={submitBlockCheck}
            >
              {isLastBlock ? "完成实验" : "继续下一部分 →"}
            </button>
          </section>
        ) : null}

        {screen === "done" ? (
          <section className="flow-panel debrief-shell">
            <p className="eyebrow">完成</p>
            <h2>感谢你的参与</h2>
            {completionCode ? (
              <p className="debrief-code">完成码：<strong>{completionCode}</strong></p>
            ) : null}

            {participant ? (
              <ResultsPanel
                trialLog={trialLog}
                stance={participant.stance}
                intensity={participant.intensity}
              />
            ) : null}

            <div className="debrief-authors">
              <div className="debrief-author-card">
                <div className="debrief-author-header">
                  <span className="debrief-author-name">小马</span>
                  <span className="debrief-author-role">主题选定 · 材料准备 · 理论构建 · 实验设计</span>
                </div>
                <p>感谢大家参与这项试验，你们帮助我们太多了。无论您在实验中的判断如何，您的参与本身就是对科学研究的宝贵贡献。实验中呈现的言论不代表研究团队的立场，您的反应也绝不定义您的人格为了保证实验结果准确。我们并没有一开始告诉大家实验属于什么具体领域也没有告诉大家我们实际想测量什么，现在实验结束所以我将告诉大家关于实验的全部信息。</p>
                <p className="debrief-highlight">这是一项社会心理学实验，关于先验态度和元认知监控对社会判断的影响。根据我们的假说，针对一个事件的更强的先验态度带来更强烈的观点认同或反对，元认知监控作为一个自我反思的机制在更强的先验态度下效果更弱。同时元认知监控的效果是通过进行对直觉的反思能促进判断的理性。实验中你所接触到的大部分材料，或多或少存在逻辑论述的微瑕（有的是大疵）。我们预计通过"回顾刚才的判断，我感觉是正确的？"这类问题，激活你的元认知系统，并强化理性系统的工作从更加权重逻辑合理的识别与处理，而影响评分的关注点与结果。中国女权主义的发展只是一个供研究的典型素材。</p>
                <p>我们在社会判断中总是充满观点和信念，或许一个人认为他/她是女权主义者，她/他是尊重女性，愿意为人人平等的社会贡献的，并且他/她相信其思考是符合逻辑，常理的。但是暴露在具体言论下，这个人可能会选择赞同一些潜在不道德，不符合逻辑的信息，比如可能是不尊重女性的信息或者相反立场的极端信息，这和他/她意识中的那些高尚的信念是相反的，这是因为，我们还有一套和态度一致的信念，很多属于非意识的信念，这种信念的调用是豪不费力的，当我们认同了一句不道德的话，一句看似有逻辑其实错误的话，这就是我们根据直觉做出了判断，其中一部分原因是调用了基于已有态度的非意识的信念进行判断。非意识可能包括"刻板印象"，"偏见"这类不合乎我们意识中高尚原则的信念，或许有人会因此批评我们不是真正的道德者，不是真正的女权主义者，不是理智的人，我拒绝这种批评，真正的我们是意识和非意识共存的，从这个角度看，成长就在不断对我们直觉，对我们那些"错误的信念"和"持续造成影响的态度"的反思中发生。互联网越来越极端，我们的基础教育也从来不关注社会判断这一话题，一次实验或许不能告诉我们应该怎么做，所谓的"警觉的"，"理性的"社会判断方式仍需要长期积累，我们可以做的就是觉察自己的直觉并愿意对其保持审视，在没有足够信息的情况下放弃对确定结论的追求，这样我们就在一次次的"理性判断"中塑造我们的信念，从而锚定自我。</p>
              </div>

              <div className="debrief-author-card">
                <div className="debrief-author-header">
                  <span className="debrief-author-name">小李</span>
                  <span className="debrief-author-role">还在撰写中。。</span>
                </div>
                <p className="debrief-placeholder">（小李的话即将加入～）</p>
              </div>

              <div className="debrief-author-card">
                <div className="debrief-author-header">
                  <span className="debrief-author-name">小Bob</span>
                  <span className="debrief-author-role">理论优化 · 实验设计 · 网站搭建 · 数据分析</span>
                </div>
                <p>哈哈感谢大家的实验参与！由于这只是一个课业作业而不是科研项目，抱歉没有资金给大家准备价值绑定的数值象征物（受试经费），只能献出我们的爱意向参与实验的各位表示感谢 ：）！</p>
                <p>这个实验是我们社会心理学social psychology的小组作业，我当时对马老师很感兴趣所以找他组队，当时他已经深思熟虑了许多，并提出了探究元认知对于原则一致性的调节作用作为主题。当时我其实蛮吃惊的，因为从表面上来说这个主题对于作业来说是比较深的，但对于现在的舆论环境和网络行为的探讨又包含意义潜力。后来得知实际上小马本身对网络舆论下的人认知变化一直都很感兴趣，也是他未来的fyp（毕业项目）的方向。研究舆论不是一件简单的事情，在看来道德相对的世界，人都处处用情感、理性捍卫着自己信任的、有感的立场。所谓存在即合理，这一直是我对任何立场的观点。原本小马把 <strong>道德基本原则</strong> ，例如不能人身攻击，人之间维持基本的解释尊重，也纳入我们这次检查的范围内，但是被我驳斥掉了。因为我觉得这些道德本质和<strong>观点与观念</strong>高度重合且，没有客观原则一说。最终我们把原则约束到了：认知警觉和理性推论与觉察这一范围内。在这个层面上，相对客观的：以偏概全、指代错位、因果混淆等错误可以被稳定定义。并选择了女权、性别议题作为背景材料，设计了这一个实验。</p>
                <p className="debrief-highlight">过程中你所接触到的关于确定性、清晰度的反思问题，及是我们用以刺激元认知监控，并预测其刺激理性判断的工具。在舆论场中，个人时常会因为论题中，生动的、情绪化的、情绪的论述，并在快速的直觉反应中产生认同、支持、反对、攻击等态度和行为，却不常积极调用理性的思考，撇开立场和情绪思考陈述逻辑的正确性。直觉思考、理性评判，在我看来无对错之分，有的只是相应的各自真实后果和代价。我会为了我的立场积极赞许逻辑瑕疵的观点，因为在我看来立场的宣言比逻辑完美但毫无批判意义的论述更为重要。我也会因为我的自己论述的逻辑错误，而压抑我表达自我、和评价他人立场的冲动。在这一点上，考虑两种思维的场景、目的和效用，才能为一个陈述作出相对合理的判决。</p>
                <p>但是普通的我们又是如此<strong>懒可爱又可怜</strong>，哪有那么多精力去应对网络的宏大叙述和强烈的立场呢？又如何在其中清楚自己的立场，到底是不是真实的自我呢？至少，我希望人能知道自己当时被调动的情绪、观点、立场、论述的内容、抗争的冲动，到底是什么；在明了自己的真实后，怀揣着一份确定活着。</p>
                <p>让我元认知下，我写的也很宏大，噼里啪啦，或许也忽略了不少真实。</p>
                <p>但我确定的是：很感谢你完成了我们的实验！有了你们的数据我们才能进一步探索，在性别议题这样一个红火的仙人掌议题下，人的元认知到底是是否有效益、又是如何调节人的理性思考，如何和人的直觉态度互动的。有了你们的参与，我们也才能对大众对于此类议题的态度和理解有新的、更现实的认识，而不只是停留在网络的印象中。<s>有了你们的参与我们才能完成好作业然后拿一个大大的A...</s></p>
              </div>
            </div>

            <div className="debrief-community">
              <p>如果亲爱的你，对实验的后续感兴趣，或是想要表达你的想法，请加入我们讨论群。</p>
              <img
                className="debrief-qrcode"
                src="/qrcode-discussion.jpg"
                alt="讨论群二维码"
              />
            </div>

            <div className="debrief-actions">
              <p>读到这个份上了，你一定很喜欢我们的实验（<s>对吗对吗对吗；</s>对吧对吧！）所以也请帮我们分享一下吧～下次Bob遇见你会给你很多好吃的和好心情~</p>
              <a
                className="debrief-download-btn"
                href="/poster-4k.png"
                download="SocialGo研究海报.png"
              >
                ↓ 下载实验海报
              </a>
            </div>
          </section>
        ) : null}

        {devMode ? (
          <aside className="dev-panel">
            <div className="dev-panel-header">
              <div>
                <p className="eyebrow">Developer</p>
                <h3>调试面板</h3>
              </div>
              <button className="secondary-button dev-close-button" type="button" onClick={() => setDevMode(false)}>
                关闭
              </button>
            </div>

            <section className="dev-group">
              <div className="dev-group-title">
                <h4>Flow</h4>
                <span>流程与跳转</span>
              </div>
              <div className="dev-grid">
                <label className="field compact-field">
                  <span>Page</span>
                  <select value={screen} onChange={(event) => jumpToScreen(event.target.value as Screen)}>
                    {FLOW_STEPS.map((step) => (
                      <option key={step.id} value={step.id}>
                        {step.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field compact-field">
                  <span>Seed</span>
                  <input value={seedInput} onChange={(event) => setSeedInput(event.target.value)} placeholder="自动生成" />
                </label>
                <label className="field compact-field">
                  <span>Stance</span>
                  <select
                    value={forcedStance}
                    onChange={(event) => setForcedStance(event.target.value as ParticipantStance | "auto")}
                  >
                    <option value="auto">auto</option>
                    <option value="feminist_leaning">feminist_leaning</option>
                    <option value="male_leaning">male_leaning</option>
                  </select>
                </label>
                <label className="field compact-field">
                  <span>Block</span>
                  <select
                    value={blockIndex}
                    disabled={!assignment}
                    onChange={(event) => {
                      const nextBlockIndex = Number(event.target.value);
                      jumpToSectionPart(nextBlockIndex, 0, currentDevPart);
                    }}
                  >
                    {(assignment?.blocks ?? []).map((block) => (
                      <option key={block.blockIndex} value={block.blockIndex}>
                        {block.blockIndex + 1} / {block.blockType}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field compact-field">
                  <span>Part</span>
                  <select
                    value={currentDevPart}
                    disabled={!currentBlock}
                    onChange={(event) => jumpToSectionPart(blockIndex, trialIndex, event.target.value as Screen)}
                  >
                    {currentDevSectionParts.map((part) => (
                      <option key={part} value={part}>
                        {DEV_BLOCK_PARTS[part as keyof typeof DEV_BLOCK_PARTS]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field compact-field">
                  <span>Trial</span>
                  <select
                    value={trialIndex}
                    disabled={!currentBlock}
                    onChange={(event) => jumpToSectionPart(blockIndex, Number(event.target.value), currentDevPart)}
                  >
                    {(currentBlock?.trials ?? []).map((trial, index) => (
                      <option key={`${trial.item_id}-${index}`} value={index}>
                        {index + 1} / {trial.item_id}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="dev-button-row">
                <button className="secondary-button" type="button" onClick={() => createOrResetAssignment()}>
                  重抽整套题
                </button>
                <button className="secondary-button" type="button" onClick={() => jumpRelativeTrial(-1)} disabled={!assignment}>
                  上一题
                </button>
                <button className="secondary-button" type="button" onClick={() => jumpRelativeTrial(1)} disabled={!assignment}>
                  下一题
                </button>
              </div>
              <div className="dev-button-row">
                <button className="secondary-button" type="button" onClick={fillTestProfile}>
                  填入测试参与者
                </button>
                <button className="secondary-button" type="button" onClick={clearSession}>
                  清空当前会话
                </button>
              </div>
              <div className="dev-button-row">
                <button className="secondary-button dev-mock-btn" type="button" onClick={previewMockResults}>
                  预览 Mock 结果页
                </button>
              </div>
            </section>

            <section className="dev-group">
              <div className="dev-group-title">
                <h4>Material</h4>
                <span>当前材料与来源</span>
              </div>
              <dl className="dev-kv">
                <div>
                  <dt>item_id</dt>
                  <dd>{currentTrial?.item_id ?? "none"}</dd>
                </div>
                <div>
                  <dt>family_id</dt>
                  <dd>{currentTrial?.family_id ?? "none"}</dd>
                </div>
                <div>
                  <dt>item_role</dt>
                  <dd>{currentTrial?.item_role ?? "none"}</dd>
                </div>
                <div>
                  <dt>stance_version</dt>
                  <dd>{currentTrial?.stance_version ?? "none"}</dd>
                </div>
                <div>
                  <dt>topic_cluster</dt>
                  <dd>{currentTrial?.topic_cluster ?? "none"}</dd>
                </div>
                <div>
                  <dt>fallacy_type</dt>
                  <dd>{currentTrial?.fallacy_type ?? "none"}</dd>
                </div>
                <div>
                  <dt>source_ids</dt>
                  <dd>{currentSourceIds}</dd>
                </div>
                <div>
                  <dt>issue_note</dt>
                  <dd>{currentTrial?.issue_note ?? "none"}</dd>
                </div>
                <div>
                  <dt>source_path</dt>
                  <dd>{MATERIALS.source_path}</dd>
                </div>
              </dl>
              <div className="dev-button-row">
                <button className="secondary-button" type="button" onClick={rerollCurrentTrial} disabled={!currentTrial}>
                  刷新当前题
                </button>
                <button className="secondary-button" type="button" onClick={saveDebugResponse}>
                  测试写入已禁用
                </button>
              </div>
            </section>

            <section className="dev-group">
              <div className="dev-group-title">
                <h4>Runtime</h4>
                <span>版本、环境与数据回退</span>
              </div>
              <div className="dev-grid">
                <label className="field compact-field">
                  <span>时限（秒）</span>
                  <input
                    type="number"
                    min="3"
                    max="60"
                    value={selectedQuestionSeconds}
                    onChange={(event) => setQuestionLimitMs(clampSeconds(Number(event.target.value)) * 1000)}
                  />
                </label>
                <label className="field compact-field toggle-field">
                  <span>自动提交</span>
                  <input
                    type="checkbox"
                    checked={autoSubmitEnabled}
                    onChange={(event) => setAutoSubmitEnabled(event.target.checked)}
                  />
                </label>
              </div>
              <dl className="dev-kv">
                <div>
                  <dt>study_version</dt>
                  <dd>{STUDY_VERSION}</dd>
                </div>
                <div>
                  <dt>screen</dt>
                  <dd>{screen}</dd>
                </div>
                <div>
                  <dt>participant_id</dt>
                  <dd>{participant?.participant_id ?? "not-started"}</dd>
                </div>
                <div>
                  <dt>assignment_seed</dt>
                  <dd>{(assignment?.seed ?? seedInput) || "none"}</dd>
                </div>
                <div>
                  <dt>host</dt>
                  <dd>{readHost()}</dd>
                </div>
                <div>
                  <dt>local participants</dt>
                  <dd>{localParticipantsCount}</dd>
                </div>
                <div>
                  <dt>local responses</dt>
                  <dd>{localResponsesCount}</dd>
                </div>
                <div>
                  <dt>local block_checks</dt>
                  <dd>{localBlockChecksCount}</dd>
                </div>
              </dl>
              <div className="dev-button-row">
                <button className="secondary-button" type="button" onClick={checkApiHealth}>
                  检查 API / D1
                </button>
                <button className="secondary-button" type="button" onClick={exportLocalFallback}>
                  导出本地回退
                </button>
              </div>
              <p className={`dev-status ${devHealth.status}`}>{devHealth.detail}</p>
              <p className="dev-status neutral">{devRuntimeMessage}</p>
            </section>
          </aside>
        ) : null}

        {devUnlockOpen ? (
          <div className="dev-unlock-backdrop">
            <section className="dev-unlock-sheet" aria-label="Developer mode unlock">
              <p className="eyebrow">Developer</p>
              <h3>输入口令</h3>
              <label className="field compact-field">
                <span>口令</span>
                <input
                  autoFocus
                  type="password"
                  value={devPasswordInput}
                  onChange={(event) => setDevPasswordInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      submitDevUnlock();
                    }
                    if (event.key === "Escape") {
                      setDevUnlockOpen(false);
                    }
                  }}
                />
              </label>
              {devUnlockError ? <p className="dev-unlock-error">{devUnlockError}</p> : null}
              <div className="dev-unlock-actions">
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => {
                    setDevUnlockOpen(false);
                    setDevUnlockError("");
                    setDevPasswordInput("");
                  }}
                >
                  取消
                </button>
                <button className="primary-button" type="button" onClick={submitDevUnlock}>
                  进入开发者模式
                </button>
              </div>
            </section>
          </div>
        ) : null}
      </section>
    </main>
  );
}
